import React, {useState, useEffect} from "react";
import {useForm} from "@refinedev/react-hook-form";
import {IResourceComponentsProps, useTranslate, useNotification} from "@refinedev/core";
import {useNavigate} from "react-router-dom";
import {
    Box,
    TextField,
    Avatar,
    IconButton,
    Typography,
    Stack,
    CircularProgress,
    useTheme,
    Card,
    CardContent,
    Button,
    Switch,
    FormControlLabel,
    Divider,
    Collapse,
    Autocomplete,
} from "@mui/material";
import {Delete} from "@mui/icons-material";
import {Building2, ArrowLeft, Radio} from "lucide-react";
import {useAuth} from "../../contexts/auth-context";
import axiosInstance from "../../api/axios-config";
import imageCompression from "browser-image-compression";

export const CompanyEdit: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const navigate = useNavigate();
    const {user: currentUser} = useAuth();
    const {open} = useNotification();

    const [imagePreview, setImagePreview] = useState<string>("");
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const {
        saveButtonProps,
        register,
        setValue,
        watch,
        formState: {errors},
        refineCore: {queryResult, onFinish},
    } = useForm();

    const record = queryResult?.data?.data;
    const isModerator = currentUser?.role === 'MODERATOR' || currentUser?.role === 'MODERATOR_FREE';
    const hideAdvancedFields = isModerator;
    const canEditTeleSurveilleur = currentUser?.role === 'ADMIN' || currentUser?.role === 'SAFEE_STAFF';

    // Monitoring providers state
    const [monitoringProviders, setMonitoringProviders] = useState<Array<{ id: number; code: string; name: string }>>([]);

    // Watch télésurveilleur fields
    const isTeleSurveilleur = watch("isTeleSurveilleur");

    // Initialize télésurveilleur toggle from record
    useEffect(() => {
        if (record) {
            if (record.isTeleSurveilleur !== undefined) {
                setValue("isTeleSurveilleur", record.isTeleSurveilleur);
            }
        }
    }, [record, setValue]);

    // Set initial logo from record
    useEffect(() => {
        if (record?.logo && !imagePreview) {
            setImagePreview(record.logo);
        }
    }, [record?.logo, imagePreview]);

    // Fetch monitoring providers for the select dropdown
    useEffect(() => {
        if (!canEditTeleSurveilleur) return;
        axiosInstance.get("/monitoring/providers").then((res) => {
            if (res.data?.data) {
                setMonitoringProviders(res.data.data);
            }
        }).catch(() => {
            // Silently ignore — user may not have access
        });
    }, [canEditTeleSurveilleur]);

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        try {
            // Client-side compression
            const options = {
                maxSizeMB: 1,
                maxWidthOrHeight: 500,
                useWebWorker: true,
                fileType: "image/jpeg" as const,
            };

            const compressedFile = await imageCompression(file, options);

            // Preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(compressedFile);

            // Upload to S3
            const extension = "jpg"; // Always use jpg after compression
            const {data} = await axiosInstance.get("/aws/s3-signed-url/put", {
                params: {folder: "entreprises", extension},
            });

            await fetch(data.url, {
                method: "PUT",
                body: compressedFile,
                headers: {"Content-Type": "image/jpeg"},
            });

            setValue("logo", data.path);

            open?.({
                type: "success",
                message: translate("company.logo.uploadSuccess", "Logo uploaded successfully"),
            });
        } catch (error) {
            console.error("Error uploading image:", error);
            open?.({
                type: "error",
                message: translate("company.logo.uploadError", "Failed to upload logo"),
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveLogo = () => {
        setImagePreview("");
        setValue("logo", null);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const formData = watch();
            await onFinish(formData);
        } finally {
            setIsSaving(false);
        }
    };

    const currentLogo = watch("logo");

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </IconButton>
                <Typography variant="h4" fontWeight={600}>
                    {translate("company.edit.title", "Modifier l'organisation")}
                </Typography>
            </Stack>

            <Card elevation={2}>
                <CardContent sx={{ p: 4 }}>
                    {/* Logo Upload Section */}
                    <Box sx={{ textAlign: "center", mb: 4 }}>
                        <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="logo-upload"
                            type="file"
                            onChange={handleImageChange}
                            disabled={isUploading}
                        />

                        <Stack
                            direction="column"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <label htmlFor="logo-upload">
                                <IconButton
                                    color="primary"
                                    component="span"
                                    disabled={isUploading}
                                    sx={{ p: 0 }}
                                >
                                    <Avatar
                                        src={imagePreview || currentLogo || undefined}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            cursor: "pointer",
                                            border: `3px solid ${theme.palette.primary.main}`,
                                            bgcolor: theme.palette.grey[100],
                                            "&:hover": { opacity: 0.8 },
                                        }}
                                    >
                                        {isUploading ? (
                                            <CircularProgress size={40} />
                                        ) : (
                                            <Building2 size={40} color={theme.palette.grey[400]} />
                                        )}
                                    </Avatar>
                                </IconButton>
                            </label>

                            {(imagePreview || currentLogo) && (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                    onClick={handleRemoveLogo}
                                    sx={{
                                        cursor: isUploading ? "default" : "pointer",
                                        color: theme.palette.error.main,
                                        opacity: isUploading ? 0.5 : 1,
                                        "&:hover": { opacity: isUploading ? 0.5 : 0.7 },
                                    }}
                                >
                                    <Delete fontSize="small" />
                                    <Typography variant="caption" color="error">
                                        {translate("company.logo.remove", "Supprimer le logo")}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            {translate(
                                "company.logo.hint",
                                "Cliquez pour changer le logo de l'organisation"
                            )}
                        </Typography>
                    </Box>

                    {/* Hidden input for logo path */}
                    <input type="hidden" {...register("logo")} />

                    {/* Form Fields */}
                    <Stack spacing={3}>
                        {!hideAdvancedFields && (
                            <TextField
                                {...register("id", {
                                    required: "This field is required",
                                    valueAsNumber: true,
                                })}
                                error={!!(errors as any)?.id}
                                helperText={(errors as any)?.id?.message}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                type="number"
                                label={translate("company.fields.id", "ID")}
                                name="id"
                                disabled
                            />
                        )}

                        <TextField
                            {...register("nom", {
                                required: "This field is required",
                            })}
                            error={!!(errors as any)?.nom}
                            helperText={(errors as any)?.nom?.message}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            type="text"
                            label={translate("company.fields.name", "Nom de l'organisation")}
                            name="nom"
                        />

                        {!hideAdvancedFields && (
                            <TextField
                                {...register("limitUser", {
                                    required: "This field is required",
                                })}
                                error={!!(errors as any)?.limitUser}
                                helperText={(errors as any)?.limitUser?.message}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                type="number"
                                label={translate("company.fields.limit", "Limite d'utilisateurs")}
                                name="limitUser"
                            />
                        )}
                    </Stack>

                    {/* Télésurveilleur Section - ADMIN and SAFEE_STAFF only */}
                    {canEditTeleSurveilleur && (
                        <>
                            <Divider sx={{ my: 4 }} />

                            <Box>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                    <Radio size={24} color={theme.palette.primary.main} />
                                    <Typography variant="h6" fontWeight={600}>
                                        {translate("company.telesurveilleur.title", "Configuration Télésurveilleur")}
                                    </Typography>
                                </Stack>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {translate(
                                        "company.telesurveilleur.description",
                                        "Activez le mode télésurveilleur pour configurer le format des messages d'alerte envoyés aux centres de télésurveillance."
                                    )}
                                </Typography>

                                {/* Hidden input for form registration */}
                                <input type="hidden" {...register("isTeleSurveilleur")} />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isTeleSurveilleur || false}
                                            onChange={(e) => {
                                                setValue("isTeleSurveilleur", e.target.checked);
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label={translate("company.telesurveilleur.enable", "Activer le mode télésurveilleur")}
                                />

                                <Collapse in={isTeleSurveilleur}>
                                    <Box sx={{ mt: 2 }}>
                                        <input type="hidden" {...register("monitoringProviderId")} />
                                        <Autocomplete
                                            options={monitoringProviders}
                                            getOptionLabel={(option) => `${option.code} — ${option.name}`}
                                            value={monitoringProviders.find((p) => p.id === watch("monitoringProviderId")) || null}
                                            onChange={(_event, newValue) => {
                                                setValue("monitoringProviderId", newValue ? newValue.id : null);
                                            }}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={translate("company.monitoring.monitoringProviderId", "Provider de télésurveillance")}
                                                    InputLabelProps={{ shrink: true }}
                                                    placeholder={translate("company.monitoring.selectProvider", "Aucun (sélectionner un provider)")}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Collapse>
                            </Box>
                        </>
                    )}

                    {/* Actions */}
                    <Stack direction="row" spacing={2} sx={{ mt: 4 }} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => navigate(-1)}
                            disabled={isSaving || isUploading}
                        >
                            {translate("common.cancel", "Annuler")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSaving || isUploading}
                        >
                            {isSaving ? <CircularProgress size={24} /> : translate("common.save", "Enregistrer")}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};
